/**
 * Onboarding — /onboarding route.
 *
 * Real multi-step wizard for new tenants (B0.41). Mirrors the three
 * onboarding paths from docs/00_PLATFORM_PLAN.md § Organizational
 * structure plus the gateway-claim handoff from Doc A § 3.
 *
 * Steps:
 *   1. Welcome — pick guided wizard vs. demo-only walkthrough
 *   2. Org & site — name, region (single-site default; multi-site is
 *      Phase 1)
 *   3. Industry pack — Foundry / Pharma / Food / Decide later. Selecting
 *      a pack enables it live (B0.25 toggle) and optionally applies the
 *      pack's matching visual theme (B0.39 presets)
 *   4. Plant time (foundry orgs only, 2026-10-01): the plant's zone (the
 *      browser's is offered, never filled in), when its production day
 *      starts (on its own date, or the evening before), and its shifts, or
 *      "Set this later". Every day, shift and week is cut on it. Other orgs
 *      skip it.
 *   5. Gateway claim: mint a one-time claim code (base64url), show install
 *      instructions, with a "skip — claim later" escape hatch
 *   6. Done: summary + "open dashboards" CTA
 *
 * Progress is persisted in localStorage so a reload returns the user
 * to the right step. "Skip for now" in the top bar bails to /app at
 * any point — important for sales demos.
 *
 * Phase-1 wires the real submit handlers:
 *   - Step 2  → POST /v1/platform/orgs
 *   - Step 3  → POST /v1/platform/packs/{id}/enable (already wired in
 *               B0.25's TenantThemeAdmin.togglePack)
 *   - Step 4  → PUT /v1/twin/plant-time, after the org exists (the
 *               creator is its admin), unless "Set this later"
 *   - Step 5  → POST /v1/things/{id}/claim → returns the claim
 *               code + the JWT-signed bootstrap URL the gateway image
 *               consumes on first boot.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  Cpu,
  Eye,
  Factory,
  GraduationCap,
  LayoutDashboard,
  Loader2,
  PackageOpen,
  RefreshCw,
  Rocket,
  Sparkles,
} from "lucide-react";
import { BrandMark } from "@/lib/ui/brand-mark";
import { Button } from "@/lib/ui/button";
import { Badge } from "@/lib/ui/badge";
import { Input } from "@/lib/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/lib/ui/card";
import { cn } from "@/lib/ui/cn";
import { WizardLayout } from "@/lib/ui/wizard-layout";
import { useTenantTheme, useTenantThemeAdmin } from "@/lib/tenant-theme";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { useToast } from "@/lib/ui/toast";
import { isOidcConfigured } from "@/lib/oidc-config";
import { orgsApi, packsApi, sitesApi, things } from "@/lib/api";
import { ApiClientError } from "@/lib/api-client";
import { usePlant } from "@/lib/plant/plant-store";
import {
  GROUP_ORDER,
  TUTORIALS,
  type TutorialDefinition,
  type TutorialGroup,
} from "@/lib/tutorials/definitions";
import { getDoneIds } from "@/lib/tutorials/progress";
import { TutorialCard } from "@/lib/tutorials/TutorialCard";
import { useSavePlantTime } from "@/lib/api/plant-time";
import { checkWizardOrg, enablePackStep, savePlantTimeStep, wizardOrgErrorText } from "@/lib/onboarding-writes";
import { PlantTimeFields, TodaysShiftsPreview } from "@/components/plant-time/PlantTimeFields";
import {
  browserZone,
  formOf,
  formProblems,
  plantTimeLine,
  plantTimeSaveErrorText,
  timeZoneOptions,
  type PlantTimeForm,
} from "@/lib/plant-time-form";
import { plantTimeApi, type PlantTime } from "@/lib/shift-calendar";

// ────────────────────────────────────────────────────────────────────
// Types + constants
// ────────────────────────────────────────────────────────────────────

type StepIndex = 1 | 2 | 3 | 4 | 5 | 6;

/** The step numbers by name: the Plant time step (4) is a foundry org's only. */
const STEP_PLANT_TIME = 4;
const STEP_CONNECT = 5;
const STEP_DONE = 6;

/** The version of the persisted state: 2 added the Plant time step (a state saved before it numbers 4 Connect, 5 Done). */
const STATE_VERSION = 2;

/** A new org's plant time: nothing set yet. */
const NO_PLANT_TIME: PlantTime = { timezone: "", shift_calendar: [], production_day_start: "", production_week_start: null };

interface OnboardingState {
  version: number;
  step: StepIndex;
  orgName: string;
  siteName: string;
  region: string;
  /** 'real' = the customer's production plant; 'demo' = a sample sandbox. */
  dataMode: "real" | "demo";
  pickedPack: "foundry" | "none" | null;
  applyPackTheme: boolean;
  claimCode: string | null;
  completed: boolean;
  // Server ids, set as each real resource is created (live-auth mode). These
  // make the steps idempotent across Back/Next + a reload: an org/site/gateway
  // is created once, then reused.
  orgId: string | null;
  siteId: string | null;
  gatewayId: string | null;
  claimExpiresAt: string | null;
  /** The plant time as the Plant time step edits it (null: not opened yet, the browser's zone at midnight). */
  plantTime: PlantTimeForm | null;
  /** "Set this later": the step saves nothing, and the Plant time page sets it. */
  plantTimeLater: boolean;
  /** The plant time was saved for the new org. */
  plantTimeSaved: boolean;
}

const STORAGE_KEY = "divineiot:onboarding-state";

const REGIONS = [
  { id: "ap-south-1", label: "Asia Pacific · Mumbai (ap-south-1)" },
  { id: "ap-southeast-1", label: "Asia Pacific · Singapore (ap-southeast-1)" },
  { id: "eu-west-1", label: "Europe · Dublin (eu-west-1)" },
  { id: "us-east-1", label: "US East · N. Virginia (us-east-1)" },
  { id: "us-west-2", label: "US West · Oregon (us-west-2)" },
];

const PACKS = [
  {
    id: "foundry" as const,
    name: "Foundry",
    blurb: "Induction melting, sand prep, moulding, pour, QC, sand testing.",
    ships: "v1 — production-ready",
    tier: "Tier 2",
    theme: "foundry-amber",
  },
  {
    id: "none" as const,
    name: "Decide later",
    blurb: "Tier-1 IIoT only. You can enable a pack any time from Admin.",
    ships: "always available",
    tier: "Tier 1",
    theme: null,
  },
];

const DEFAULT_STATE: OnboardingState = {
  version: STATE_VERSION,
  step: 1,
  orgName: "",
  siteName: "",
  region: "ap-south-1",
  // Fail closed: an undeclared org is treated as REAL, so nothing is invented.
  dataMode: "real" as const,
  pickedPack: null,
  applyPackTheme: true,
  claimCode: null,
  completed: false,
  orgId: null,
  siteId: null,
  gatewayId: null,
  claimExpiresAt: null,
  plantTime: null,
  plantTimeLater: false,
  plantTimeSaved: false,
};

// ────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────

export function Onboarding() {
  const navigate = useNavigate();
  const tenant = useTenantTheme();
  const admin = useTenantThemeAdmin();
  const { refreshIdentity } = useAuth();
  const { setActiveOrg, activeOrgId } = useOrg();
  const toast = useToast();

  // Plant model for the active org (#120). PlantProvider is mounted at the app
  // root (inside OrgProvider), so this is available here and — in live mode —
  // hydrates from /v1/twin/{entities,connections}. We gate "Finish" on a plant
  // actually existing for the foundry path, so onboarding can't complete with
  // an empty twin. The query re-fetches on window focus / remount, so when the
  // user builds a plant in the builder and returns to /onboarding, nodes is
  // populated and Finish unlocks.
  const { nodes: plantNodes } = usePlant();
  const hasPlant = plantNodes.length > 0;

  // live = real Zitadel auth is configured (deployed SPA). When false
  // (Arjun's UI-only stub loop), the wizard stays purely local — the org/
  // pack/gateway calls would 401 without a token, so we skip them and keep
  // the local makeClaimCode() fallback.
  const live = isOidcConfigured();

  const [state, setState] = useState<OnboardingState>(() => loadState());
  const [busy, setBusy] = useState(false);

  // Persist state on every change.
  useEffect(() => {
    saveState(state);
  }, [state]);

  const update = (patch: Partial<OnboardingState>) =>
    setState((s) => ({ ...s, ...patch }));

  // The Plant time step is a foundry org's only: any other org goes from the pack straight to Connect, and back.
  const foundry = state.pickedPack === "foundry";
  const goNext = () => {
    const next = state.step === STEP_PLANT_TIME - 1 && !foundry ? STEP_CONNECT : state.step + 1;
    update({ step: Math.min(STEP_DONE, next) as StepIndex });
  };
  const goBack = () => {
    const back = state.step === STEP_CONNECT && !foundry ? STEP_PLANT_TIME - 1 : state.step - 1;
    update({ step: Math.max(1, back) as StepIndex });
  };
  const savePlantTime = useSavePlantTime();
  const zone = browserZone();
  const plantForm = state.plantTime ?? formOf(NO_PLANT_TIME);
  // Every write below goes to the active org, so each first checks that it is the org this wizard created (the wizard
  // resumes from the browser, and the org switcher can change the active org under it): onboarding-writes.
  const wizard = { live, orgId: state.orgId, orgName: state.orgName };

  const handlePickPack = (id: OnboardingState["pickedPack"]) => {
    update({ pickedPack: id });
    // Local UX: toggle the pack on the tenant theme so the Tier-2 nav
    // appears immediately. The server-side enable happens on the step-3
    // Next (ensurePackEnabled) so it persists per-org.
    if (id && id !== "none") {
      if (!tenant.enabledPacks.includes(id)) admin.togglePack(id);
    } else if (id === "none") {
      // If "none" picked but pack was previously enabled, leave it — user
      // can still flip it off in Admin. This avoids surprising removals.
    }
    // Optionally apply the pack's matching visual theme.
    if (state.applyPackTheme && id && id !== "none") {
      const pack = PACKS.find((p) => p.id === id);
      if (pack?.theme) admin.applyPreset(pack.theme);
    }
  };

  // Step 2 → create the org (once), make it active, and refresh memberships
  // so the OrgProvider sees it (otherwise its reconcile would bounce the
  // active org back to the user's first existing membership).
  const ensureOrg = async () => {
    if (!live || state.orgId) return;
    const org = await orgsApi.create({
      name: state.orgName.trim(),
      data_mode: state.dataMode,
    });
    update({ orgId: org.id });
    await refreshIdentity();
    setActiveOrg(org.id);
  };

  // Step 3 → persist the chosen pack for the wizard's org, which must be the active one.
  const ensurePackEnabled = async () => {
    await enablePackStep(wizard, activeOrgId, state.pickedPack, packsApi.enable);
    // The pack's look, applied on the pick, is a draft for the wizard's org until saved: its creator is its admin.
    if (live && state.applyPackTheme && activeOrgId === state.orgId && admin.orgLook.dirty && admin.orgLook.canSave) {
      if (!(await admin.orgLook.save())) {
        toast.push("error", "The pack's look was not saved for the org. Set it in Settings, Organisation.");
      }
    }
  };

  // Step 4 → the wizard org's plant time (its creator is its admin), unless the person chose to set it later on the
  // Plant time page. Only what differs from what the org has saved is sent, and nothing when the form is unchanged since
  // it was saved (a later edit on the Plant time page is never undone by a Next).
  const ensurePlantTime = async () => {
    await savePlantTimeStep(
      wizard,
      activeOrgId,
      { later: state.plantTimeLater, alreadySaved: state.plantTimeSaved, form: plantForm },
      { read: plantTimeApi.get, save: (change) => savePlantTime.mutateAsync(change) },
    );
    if (!live || state.plantTimeLater) return;
    update({ plantTimeSaved: true, plantTime: plantForm });
  };

  const handleNext = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (state.step === 2) await ensureOrg();
      else if (state.step === 3) await ensurePackEnabled();
      else if (state.step === STEP_PLANT_TIME) await ensurePlantTime();
      goNext();
    } catch (e) {
      toast.push("error", onboardingErrorMessage(e, state.step));
    } finally {
      setBusy(false);
    }
  };

  // Step 4 → build the org's first site + gateway (once) and mint a real,
  // one-time claim code. Regenerate just re-claims the same gateway. In
  // stub mode, fall back to the cosmetic local code.
  const handleGenerateClaim = async () => {
    if (busy) return;
    if (!live) {
      update({ claimCode: makeClaimCode() });
      return;
    }
    setBusy(true);
    try {
      checkWizardOrg(wizard, activeOrgId);
      let siteId = state.siteId;
      if (!siteId) {
        const site = await sitesApi.create({
          name: state.siteName.trim() || "Primary site",
          slug: slugify(state.siteName) || "primary-site",
        });
        siteId = site.id;
        update({ siteId });
      }
      let gwId = state.gatewayId;
      if (!gwId) {
        // P6: a Thing is an instance of a gateway product. A real hardware
        // gateway being claimed here defaults to the generic-mqtt product
        // (its field protocol is configured later in the field app); no
        // simulation, no authored tags — those arrive once it connects.
        const gw = await things.create({
          product_id: "generic-mqtt",
          site_id: siteId,
          name: "Gateway 1",
          simulate: false,
          tags: [],
        });
        gwId = gw.id;
        update({ gatewayId: gwId });
      }
      const claim = await things.claim(gwId);
      update({ claimCode: claim.claim_code, claimExpiresAt: claim.expires_at });
    } catch (e) {
      toast.push("error", onboardingErrorMessage(e, STEP_CONNECT));
    } finally {
      setBusy(false);
    }
  };

  const finish = () => {
    update({ completed: true, step: STEP_DONE });
    // Don't auto-navigate; the user clicks "Open dashboards" themselves.
  };

  const navigateToBuilder = () => navigate("/app/twin/build");

  // An invited user whose stashed token was lost across the sign-in round-trip
  // lands here (RequireOrg → /onboarding) instead of auto-joining. Rather than
  // force them to CREATE a new org, give them an explicit path to JOIN the org
  // they were invited to: paste the invite link/code → redeem at /accept-invite.
  const handleJoinViaInvite = (raw: string) => {
    const token = extractInviteToken(raw);
    if (token) navigate(`/accept-invite?token=${encodeURIComponent(token)}`);
    else toast.push("error", "That doesn't look like a valid invite link or code.");
  };

  // #120 — a foundry org must actually have a plant (entities/connections)
  // before onboarding can finish. The plant is built in the dedicated builder
  // (/app/twin/build); the user returns here and finishes once it exists. For
  // the "Decide later" (Tier-1-only) path there's no plant concept, so Finish
  // stays open. hasPlant is always true in stub mode (the fixture plant seeds
  // the store), so the local UI-only dev loop is unaffected.
  const requiresPlant = state.pickedPack === "foundry";
  const finishBlockedOnPlant = requiresPlant && !hasPlant;

  const canAdvance: Record<StepIndex, boolean> = {
    1: true,
    2: state.orgName.trim().length > 1 && state.siteName.trim().length > 1,
    3: state.pickedPack !== null,
    4: state.plantTimeLater || formProblems(plantForm).length === 0, // the plant time reads, or is set later
    5: true, // gateway is skippable (#121)
    6: true,
  };
  // The steps this org walks: the Plant time step only for a foundry org.
  const walked = STEPS.filter((s) => s.i !== STEP_PLANT_TIME || foundry);
  const position = walked.findIndex((s) => s.i === state.step) + 1;

  return (
    <div className="flex h-dvh flex-col bg-surface">
      {/* Top bar */}
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-surface-elevated px-4 md:px-10">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark />
          <span className="text-base font-semibold text-on-surface">
            {tenant.productName}
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="uppercase tracking-wide">
            Onboarding
          </Badge>
          <Button variant="ghost" asChild>
            <Link to="/app">
              <Eye className="mr-1.5 size-4" />
              Skip for now
            </Link>
          </Button>
        </div>
      </header>

      <WizardLayout
        stepKey={state.step}
        footer={
          // Footer nav, pinned to the bottom of the window: the steps scroll above it.
          state.step < STEP_DONE && (
            <div className="mx-auto flex w-full max-w-3xl items-center justify-between px-4 py-3 md:px-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={goBack}
                disabled={state.step === 1}
              >
                <ArrowLeft className="mr-1.5 size-4" />
                Back
              </Button>
              <div className="text-[11px] text-on-surface-muted">
                Step {position} of {walked.length}
              </div>
              {state.step < STEP_CONNECT ? (
                <Button
                  size="sm"
                  onClick={() => void handleNext()}
                  disabled={!canAdvance[state.step] || busy}
                >
                  {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
                  Next
                  <ArrowRight className="ml-1.5 size-4" />
                </Button>
              ) : finishBlockedOnPlant ? (
                // #120 — foundry org with no plant yet: send them to build it.
                // Finish is unavailable until a plant exists; this CTA opens the
                // builder, and they return here to finish.
                <Button size="sm" onClick={navigateToBuilder} disabled={busy}>
                  <Factory className="mr-1.5 size-4" />
                  Build your plant
                  <ArrowRight className="ml-1.5 size-4" />
                </Button>
              ) : (
                <Button size="sm" onClick={finish} disabled={busy}>
                  Finish
                  <Rocket className="ml-1.5 size-4" />
                </Button>
              )}
            </div>
          )
        }
      >
        <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-10 md:px-6 md:py-14">
          <ProgressStrip current={state.step} steps={walked} />

          <div>
            {state.step === 1 && (
              <WelcomeStep
                onContinue={goNext}
                onJoinViaInvite={handleJoinViaInvite}
              />
            )}
            {state.step === 2 && (
              <OrgStep
                orgName={state.orgName}
                siteName={state.siteName}
                region={state.region}
                dataMode={state.dataMode}
                dataModeLocked={Boolean(state.orgId)}
                onOrgName={(v) => update({ orgName: v })}
                onSiteName={(v) => update({ siteName: v })}
                onRegion={(v) => update({ region: v })}
                onDataMode={(v) => update({ dataMode: v })}
              />
            )}
            {state.step === 3 && (
              <PackStep
                picked={state.pickedPack}
                applyTheme={state.applyPackTheme}
                onPick={handlePickPack}
                onToggleApplyTheme={(v) => update({ applyPackTheme: v })}
              />
            )}
            {state.step === STEP_PLANT_TIME && (
              <PlantTimeStep
                form={plantForm}
                browser={zone}
                zones={timeZoneOptions([plantForm.zone, zone])}
                later={state.plantTimeLater}
                busy={busy}
                error={savePlantTime.isError ? plantTimeSaveErrorText(savePlantTime.error) : null}
                onChange={(f) => {
                  savePlantTime.reset();
                  update({ plantTime: f, plantTimeSaved: false });
                }}
                onLater={(later) => update({ plantTimeLater: later })}
              />
            )}
            {state.step === STEP_CONNECT && (
              <GatewayStep
                claimCode={state.claimCode}
                onGenerate={() => void handleGenerateClaim()}
                onSkip={finishBlockedOnPlant ? navigateToBuilder : finish}
                busy={busy}
                orgName={state.orgName || "your organisation"}
                isDemo={state.dataMode === "demo"}
              />
            )}
            {state.step === STEP_DONE && (
              <DoneStep
                state={state}
                hasPlant={hasPlant}
                onOpenApp={() => navigate("/app")}
                onBuildPlant={navigateToBuilder}
                onOpenGraph={() => navigate("/app/twin")}
                onResetOnboarding={() => {
                  clearState();
                  setState(DEFAULT_STATE);
                }}
              />
            )}
          </div>

          {/* #120 — on the final step, foundry orgs must build a plant before
              finishing. Spell out why "Finish" is gated and how to clear it. */}
          {state.step === STEP_CONNECT && finishBlockedOnPlant && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-on-surface">
              <Factory className="mt-0.5 size-4 shrink-0 text-warning" />
              <span>
                One last thing: your foundry needs a plant before we can finish.
                Hit <strong>Build your plant</strong> to lay it out, then come
                back here — Finish unlocks once your plant exists.
              </span>
            </div>
          )}
        </main>
      </WizardLayout>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Progress strip
// ────────────────────────────────────────────────────────────────────

const STEPS: { i: StepIndex; label: string; icon: typeof Sparkles }[] = [
  { i: 1, label: "Welcome", icon: Sparkles },
  { i: 2, label: "Organisation", icon: Building2 },
  { i: 3, label: "Pack", icon: PackageOpen },
  { i: 4, label: "Plant time", icon: Clock },
  { i: 5, label: "Connect", icon: Cpu },
  { i: 6, label: "Done", icon: CheckCircle2 },
];

function ProgressStrip({ current, steps }: { current: StepIndex; steps: typeof STEPS }) {
  return (
    <div className="flex items-center justify-between gap-2">
      {steps.map((s, idx) => {
        const Icon = s.icon;
        const done = current > s.i;
        const active = current === s.i;
        return (
          <div key={s.i} className="flex flex-1 items-center">
            <div className="flex flex-col items-center gap-1 sm:flex-row sm:gap-2">
              <div
                className={cn(
                  "flex size-7 items-center justify-center rounded-full border text-xs font-semibold transition-colors",
                  done && "border-success bg-success text-success-foreground",
                  active && "border-brand bg-brand text-brand-foreground",
                  !done && !active && "border-border bg-surface-elevated text-on-surface-muted",
                )}
              >
                {done ? <Check className="size-3.5" /> : <Icon className="size-3.5" />}
              </div>
              <span
                className={cn(
                  "hidden text-[11px] font-medium uppercase tracking-wide sm:inline",
                  active ? "text-on-surface" : "text-on-surface-muted",
                )}
              >
                {s.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                className={cn(
                  "mx-2 h-px flex-1 transition-colors",
                  current > s.i ? "bg-success" : "bg-border",
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Step 1 — Welcome
// ────────────────────────────────────────────────────────────────────

function WelcomeStep({
  onContinue,
  onJoinViaInvite,
}: {
  onContinue: () => void;
  onJoinViaInvite: (raw: string) => void;
}) {
  const [inviteRaw, setInviteRaw] = useState("");
  return (
    <Card>
      <CardHeader>
        <Badge variant="info" className="w-fit uppercase tracking-wide">
          Welcome
        </Badge>
        <CardTitle className="text-2xl">
          Three minutes to your first live reading
        </CardTitle>
        <CardDescription>
          This sets up a <strong>new organisation</strong>. We'll walk through
          a few short steps: your organisation, the industry pack (a foundry
          also sets its plant's clock and shifts), your first data source, and
          a quick summary. Everything is reversible from Admin later.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <ul className="grid gap-2 text-sm md:grid-cols-2">
          {[
            "Single site is the default — multi-site lands in Admin.",
            "Pick a pack now or browse them later from Admin → Packs.",
            "Connect equipment, upload the spreadsheets you already keep, or key readings in from the floor.",
            "Your dashboards, alarms and AI casting consultant wire to that live data automatically.",
          ].map((s) => (
            <li key={s} className="flex items-start gap-2 text-on-surface-muted">
              <Check className="mt-0.5 size-4 shrink-0 text-success" />
              <span>{s}</span>
            </li>
          ))}
        </ul>

        {/* Invited-user escape hatch — a teammate's invite should JOIN their
            org, not create a new one. If the stashed token was lost across the
            sign-in round-trip, this lets the invitee paste their link and join
            instead of being forced through new-org creation. */}
        <div className="flex flex-col gap-2 rounded-md border border-dashed border-border bg-surface-elevated/40 p-4">
          <span className="text-sm font-medium text-on-surface">
            Were you invited to an existing organisation?
          </span>
          <span className="text-xs text-on-surface-muted">
            Don't create a new one — paste your invite link (or code) to join
            your teammate's organisation instead.
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={inviteRaw}
              onChange={(e) => setInviteRaw(e.target.value)}
              placeholder="Paste invite link or code"
              className="max-w-md flex-1"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => onJoinViaInvite(inviteRaw)}
              disabled={!inviteRaw.trim()}
            >
              Join organisation
              <ArrowRight className="ml-1.5 size-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <span className="text-xs text-on-surface-muted">
            In a hurry? "Skip for now" in the top right opens the demo app
            with foundry data already loaded.
          </span>
          <Button size="sm" onClick={onContinue}>
            Let's go
            <ArrowRight className="ml-1.5 size-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// Step 2 — Organisation
// ────────────────────────────────────────────────────────────────────

function OrgStep({
  orgName,
  siteName,
  region,
  dataMode,
  dataModeLocked,
  onOrgName,
  onSiteName,
  onRegion,
  onDataMode,
}: {
  orgName: string;
  siteName: string;
  region: string;
  dataMode: "real" | "demo";
  /**
   * True once the org row exists. The mode is written at creation and the
   * wizard never re-sends it, so letting someone change the selection after
   * that would show one thing on screen while the system held another.
   */
  dataModeLocked: boolean;
  onOrgName: (v: string) => void;
  onSiteName: (v: string) => void;
  onRegion: (v: string) => void;
  onDataMode: (v: "real" | "demo") => void;
}) {
  return (
    <Card>
      <CardHeader>
        <Badge variant="info" className="w-fit uppercase tracking-wide">
          Step 2
        </Badge>
        <CardTitle className="text-2xl">Tell us about your organisation</CardTitle>
        <CardDescription>
          Single site is the default — most customers start here. You can add
          sites, sub-customers and regional hierarchy any time from{" "}
          <code className="font-mono text-on-surface">Admin → Org hierarchy</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <LabelledField label="Organisation name">
          <Input
            value={orgName}
            onChange={(e) => onOrgName(e.target.value)}
            placeholder="Acme Castings Pvt. Ltd."
            autoFocus
          />
        </LabelledField>
        <LabelledField label="Primary site name">
          <Input
            value={siteName}
            onChange={(e) => onSiteName(e.target.value)}
            placeholder="Pune Foundry — Unit 1"
          />
        </LabelledField>

        {/*
          The real-vs-demo declaration. This is the most consequential thing a
          new tenant chooses and it used to be silently defaulted, so a customer
          setting up their actual plant was never told the distinction existed —
          and the next step then invited them to stream simulated readings into
          it. A real org refuses generated heats, mints no simulated gateways,
          and never offers Simulate.
        */}
        <LabelledField label="What is this organisation for?">
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => onDataMode("real")}
              disabled={dataModeLocked}
              aria-pressed={dataMode === "real"}
              className={cn(
                "flex-1 rounded-md border-2 p-3 text-left transition",
                dataMode === "real"
                  ? "border-brand bg-brand/10 shadow-sm"
                  : "border-border bg-surface hover:border-on-surface-muted",
                dataModeLocked && "cursor-not-allowed opacity-70",
              )}
            >
              <span className="flex items-center justify-between gap-2 text-sm font-medium text-on-surface">
                My real plant
                {dataMode === "real" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand">
                    <Check className="size-3" aria-hidden />
                    Selected
                  </span>
                )}
              </span>
              <span className="mt-0.5 block text-[11px] text-on-surface-muted">
                Production records only. Nothing is ever invented — sample data
                and simulated readings stay switched off.
              </span>
            </button>
            <button
              type="button"
              onClick={() => onDataMode("demo")}
              disabled={dataModeLocked}
              aria-pressed={dataMode === "demo"}
              className={cn(
                "flex-1 rounded-md border-2 p-3 text-left transition",
                dataMode === "demo"
                  ? "border-brand bg-brand/10 shadow-sm"
                  : "border-border bg-surface hover:border-on-surface-muted",
                dataModeLocked && "cursor-not-allowed opacity-70",
              )}
            >
              <span className="flex items-center justify-between gap-2 text-sm font-medium text-on-surface">
                Trying it out
                {dataMode === "demo" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand">
                    <Check className="size-3" aria-hidden />
                    Selected
                  </span>
                )}
              </span>
              <span className="mt-0.5 block text-[11px] text-on-surface-muted">
                A sandbox with simulated data so you can see the product working
                before your own data arrives. Clearly labelled as sample.
              </span>
            </button>
          </div>
          <p className="mt-1 text-[11px] text-on-surface-muted">
            {dataModeLocked
              ? "Already set for this organisation — change it from Admin, where switching a sandbox to a real plant also purges the sample data."
              : "You can switch a sandbox to a real plant later — the sample data is purged when you do."}
          </p>
        </LabelledField>
        <LabelledField label="Data residency">
          <select
            value={region}
            onChange={(e) => onRegion(e.target.value)}
            className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm text-on-surface"
          >
            {REGIONS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-on-surface-muted">
            We store your tenant data only in this region. GDPR + India DPDP
            compliance.
          </p>
        </LabelledField>
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// Step 3 — Industry pack
// ────────────────────────────────────────────────────────────────────

function PackStep({
  picked,
  applyTheme,
  onPick,
  onToggleApplyTheme,
}: {
  picked: OnboardingState["pickedPack"];
  applyTheme: boolean;
  onPick: (id: OnboardingState["pickedPack"]) => void;
  onToggleApplyTheme: (v: boolean) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <Badge variant="info" className="w-fit uppercase tracking-wide">
          Step 3
        </Badge>
        <CardTitle className="text-2xl">Pick an industry pack</CardTitle>
        <CardDescription>
          The Foundry pack ships its own entity templates, dashboards, AI casting consultant
          grounding and theme. Choose it to build your plant now, or decide
          later — enable any pack later from Admin.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Larger, clearer option cards (issue #99 — the old cards used 9–11px
            text that was unreadable at typical browser zoom). Each card now
            leads with an icon, uses base-size copy, and gets generous padding
            + a min-height so the two choices are easy to scan and tap. */}
        <div className="grid gap-3 sm:grid-cols-2">
          {PACKS.map((p) => {
            const active = picked === p.id;
            const ready = p.ships.startsWith("v1") || p.id === "none";
            const PackIcon = p.id === "foundry" ? Factory : PackageOpen;
            return (
              <button
                key={p.id}
                type="button"
                aria-pressed={active}
                onClick={() => ready && onPick(p.id)}
                disabled={!ready}
                className={cn(
                  "flex min-h-[160px] flex-col gap-3 rounded-xl border-2 p-5 text-left transition-colors",
                  active && "border-brand bg-brand-muted/30 ring-1 ring-brand/40",
                  !active && ready && "border-border bg-surface hover:border-brand/60",
                  !ready && "border-border bg-surface-muted/60 opacity-60",
                )}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      "flex size-11 shrink-0 items-center justify-center rounded-lg",
                      active ? "bg-brand text-brand-foreground" : "bg-brand-muted text-brand",
                    )}
                  >
                    <PackIcon className="size-6" />
                  </div>
                  <span className="flex-1 text-lg font-semibold text-on-surface">
                    {p.name}
                  </span>
                  {active && (
                    <Badge variant="default" className="shrink-0">
                      <Check className="mr-1 size-3.5" />
                      Selected
                    </Badge>
                  )}
                </div>
                <p className="flex-1 text-sm leading-relaxed text-on-surface-muted">
                  {p.blurb}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="secondary">{p.tier}</Badge>
                  <Badge variant={ready ? "success" : "warning"}>{p.ships}</Badge>
                </div>
              </button>
            );
          })}
        </div>

        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-border bg-surface-elevated/40 p-4 text-sm">
          <input
            type="checkbox"
            checked={applyTheme}
            onChange={(e) => onToggleApplyTheme(e.target.checked)}
            className="size-4"
          />
          <span className="text-on-surface">
            Apply the pack's matching visual theme
          </span>
          <span className="ml-auto text-on-surface-muted">Foundry → amber</span>
        </label>
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// Step 4: Plant time (foundry orgs)
// ────────────────────────────────────────────────────────────────────

/**
 * The plant's clock for a new foundry org: its zone (chosen, with the browser's offered as a suggestion, never filled
 * in), when its production day starts, and its shifts; or "Set this later", which saves nothing and leaves it to the
 * Plant time page. Next saves it, and is held until the zone is chosen or the step is left for later.
 */
export function PlantTimeStep({
  form,
  browser,
  zones,
  later,
  busy,
  error,
  onChange,
  onLater,
}: {
  form: PlantTimeForm;
  /** The browser's zone, offered while the form has none. */
  browser: string;
  zones: string[];
  later: boolean;
  busy: boolean;
  error: string | null;
  onChange: (next: PlantTimeForm) => void;
  onLater: (later: boolean) => void;
}) {
  const problems = later ? [] : formProblems(form);
  return (
    <Card>
      <CardHeader>
        <Badge variant="info" className="w-fit uppercase tracking-wide">
          Plant time
        </Badge>
        <CardTitle className="text-2xl">Your plant's clock</CardTitle>
        <CardDescription>
          Every day, shift and week is cut on the plant's own clock: imports read their times on it, and the briefs
          and reports name days and shifts by it. Choose the plant's own zone; your browser's is offered, but it is the
          plant's only if you are at the plant. Many three-shift plants start the day with the night shift the evening
          before: tick that below if yours does.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <label className="flex items-center gap-2 text-sm text-on-surface">
          <input
            type="checkbox"
            className="size-4 accent-brand"
            checked={later}
            onChange={(e) => onLater(e.target.checked)}
          />
          Set this later, on the Plant time page
        </label>
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,18rem)]">
          <PlantTimeFields
            form={form}
            onChange={onChange}
            zones={zones}
            disabled={later || busy}
            hideWeekStart
            suggestedZone={browser}
          />
          {!later && <TodaysShiftsPreview form={form} now={new Date()} />}
        </div>
        {problems.length > 0 && (
          <ul role="alert" className="flex list-disc flex-col gap-1 pl-5 text-sm text-danger">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** The Done summary's line for the plant time: what the step saved, or where to set it. */
function plantTimeSummary(state: Pick<OnboardingState, "pickedPack" | "plantTime" | "plantTimeLater" | "plantTimeSaved">): string {
  if (state.pickedPack !== "foundry" || state.plantTimeLater || !state.plantTimeSaved || !state.plantTime) {
    return "Set it on the Plant time page";
  }
  return plantTimeLine(state.plantTime);
}

// ────────────────────────────────────────────────────────────────────
// Step 5: Gateway claim
// ────────────────────────────────────────────────────────────────────

function GatewayStep({
  claimCode,
  onGenerate,
  onSkip,
  busy,
  orgName,
  isDemo,
}: {
  claimCode: string | null;
  onGenerate: () => void;
  onSkip: () => void;
  busy: boolean;
  orgName: string;
  /** Demo sandboxes may offer Simulate; a real plant never does. */
  isDemo: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Badge variant="info" className="w-fit uppercase tracking-wide">
            Step 4
          </Badge>
          {/* #121 — devices are optional; make that unmistakable up front. */}
          <Badge variant="secondary" className="w-fit uppercase tracking-wide">
            Optional
          </Badge>
        </div>
        <CardTitle className="text-2xl">Add your first data source</CardTitle>
        <CardDescription>
          {isDemo ? (
            <>
              A “Thing” is an instance of a SAHAS gateway product — Modbus
              TCP/RTU, OPC-UA, CAN, or 4–20 mA, plus a generic MQTT node. In this
              sandbox you author its tags and flip <strong>Simulate</strong> on to
              watch sample data flow into {orgName} immediately.{" "}
              <strong>This step is optional</strong> — you can add devices any
              time later from the Things page.
            </>
          ) : (
            <>
              {orgName} is set up as a <strong>real plant</strong>, so everything
              it shows will be production that actually happened — we never
              invent readings to fill it in. Connect your equipment, or bring in
              the records you already keep.{" "}
              <strong>This step is optional</strong> — you can do it any time
              later.
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/*
          The primary path depends on what this org IS.

          A demo sandbox exists to be explored, so Simulate is the fastest way
          to see the product work. A REAL plant must never be offered it: the
          whole promise is that what you see happened. Previously this screen
          told every new customer — real plants included — to turn Simulate on,
          which walked them into filling their own production records with
          fabricated readings.
        */}
        {isDemo ? (
          <div className="flex flex-col gap-3 rounded-md border border-dashed border-border bg-surface-elevated/40 p-5">
            <div className="flex items-center gap-2">
              <Cpu className="size-5 text-brand" />
              <span className="text-sm font-semibold text-on-surface">
                Add a Thing → pick a product → add tag rows → turn on Simulate
              </span>
            </div>
            <p className="text-sm text-on-surface-muted">
              With Simulate on, each tag streams sample data within the range you
              set — no hardware required — so your dashboards, entities and
              reports come alive instantly. Everything it produces is labelled as
              sample data.
            </p>
            <Button size="sm" asChild className="w-fit">
              <Link to="/app/gateways">
                Go to Things
                <ArrowRight className="ml-1.5 size-4" />
              </Link>
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 rounded-md border border-dashed border-border bg-surface-elevated/40 p-5">
            <div className="flex items-center gap-2">
              <Cpu className="size-5 text-brand" />
              <span className="text-sm font-semibold text-on-surface">
                Bring in your plant’s real data
              </span>
            </div>
            <p className="text-sm text-on-surface-muted">
              Three ways, and you can use all of them: connect a SAHAS gateway to
              your equipment, upload the spreadsheets you already keep (heats,
              parts, rejections, lab tests), or have operators key readings in
              from the floor on their phone. Most plants start with the
              spreadsheets they already have.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" asChild className="w-fit">
                <Link to="/app/twin/data-sources">
                  Import my data
                  <ArrowRight className="ml-1.5 size-4" />
                </Link>
              </Button>
              <Button size="sm" variant="secondary" asChild className="w-fit">
                <Link to="/app/gateways">Connect equipment</Link>
              </Button>
            </div>
          </div>
        )}

        {/* Secondary path — connect a real SAHAS gateway (claim code). */}
        <details className="rounded-md border border-border bg-surface-elevated p-3 text-xs">
          <summary className="cursor-pointer font-medium text-on-surface">
            Already have a SAHAS gateway? Connect real hardware
          </summary>
          <div className="mt-3 flex flex-col gap-3 text-on-surface-muted">
            <p>
              A gateway polls your industrial protocols (Modbus TCP/RTU,
              OPC-UA, CAN, 4–20 mA), buffers locally if connectivity drops, and
              publishes over mTLS-secured MQTT. Generate a one-time claim code
              and enter it when the gateway first connects — it provisions its
              certificate automatically.
            </p>
            {claimCode ? (
              <ClaimCodeCard code={claimCode} onRegenerate={onGenerate} busy={busy} />
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={onGenerate}
                disabled={busy}
                className="w-fit"
              >
                {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
                Generate claim code
                <ArrowRight className="ml-1.5 size-4" />
              </Button>
            )}
          </div>
        </details>

        {/* #121 — an explicit "skip / do this later" escape hatch so users
            don't think a device is mandatory to proceed. Advances exactly like
            the footer's primary action (Finish, or "Build your plant" first
            when the foundry plant gate is still open — #120). */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3 text-sm text-on-surface-muted">
          <span>No hardware handy? You can add devices later.</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onSkip}
            disabled={busy}
            className="shrink-0"
          >
            Skip — add devices later
            <ArrowRight className="ml-1.5 size-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ClaimCodeCard({
  code,
  onRegenerate,
  busy,
}: {
  code: string;
  onRegenerate: () => void;
  busy: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* permission denied — ignore */
    }
  };

  return (
    <div data-copilot-private="" className="flex flex-col gap-3 rounded-md border border-brand/40 bg-brand-muted/20 p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-brand">
          Your one-time claim code
        </span>
        <Button size="sm" variant="ghost" onClick={onRegenerate} disabled={busy}>
          {busy ? (
            <Loader2 className="mr-1.5 size-3.5 animate-spin" />
          ) : (
            <RefreshCw className="mr-1.5 size-3.5" />
          )}
          Regenerate
        </Button>
      </div>
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-4 py-3">
        <span className="break-all font-mono text-lg font-semibold tracking-wide text-on-surface">
          {code}
        </span>
        <Button size="sm" variant="outline" onClick={copy} className="shrink-0">
          {copied ? (
            <>
              <Check className="mr-1.5 size-3.5" />
              Copied
            </>
          ) : (
            <>
              <Copy className="mr-1.5 size-3.5" />
              Copy
            </>
          )}
        </Button>
      </div>
      <ol className="flex flex-col gap-1.5 pl-1 text-xs text-on-surface-muted">
        <li>1. Power on your SAHAS gateway with network access.</li>
        <li>2. Enter this code in the gateway's setup.</li>
        <li>3. It provisions its certificate and registers automatically over MQTT.</li>
      </ol>
      <p className="text-[10px] italic text-on-surface-muted">
        This code expires in 1 hour. Regenerate any time — only one code is
        active per gateway slot.
      </p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Step 5 — Done
// ────────────────────────────────────────────────────────────────────

function DoneStep({
  state,
  hasPlant,
  onOpenApp,
  onBuildPlant,
  onOpenGraph,
  onResetOnboarding,
}: {
  state: OnboardingState;
  hasPlant: boolean;
  onOpenApp: () => void;
  onBuildPlant: () => void;
  onOpenGraph: () => void;
  onResetOnboarding: () => void;
}) {
  const pack = PACKS.find((p) => p.id === state.pickedPack);
  // By the time a foundry org reaches this step the Finish gate (#120) has
  // already ensured a plant exists — so lead with "open the process graph".
  // The build CTA only resurfaces for the rare stale case (a pre-gate
  // persisted step-5 state with no plant).
  const foundryNeedsPlant = state.pickedPack === "foundry" && !hasPlant;

  return (
    <Card>
      <CardHeader>
        <Badge variant="success" className="w-fit uppercase tracking-wide">
          <CheckCircle2 className="mr-1 size-3" />
          Ready
        </Badge>
        <CardTitle className="text-2xl">You're set — let's see it run</CardTitle>
        <CardDescription>
          Everything below is live on this device. Phase 1 syncs the same
          payload through the real platform endpoints — nothing here changes
          shape.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <ul className="flex flex-col gap-2 text-sm">
          <SummaryRow
            label="Organisation"
            value={state.orgName || "—"}
            icon={Building2}
          />
          <SummaryRow
            label="Primary site"
            value={state.siteName || "—"}
            icon={Building2}
          />
          <SummaryRow
            label="Data residency"
            value={REGIONS.find((r) => r.id === state.region)?.label ?? state.region}
            icon={Building2}
          />
          <SummaryRow
            label="Industry pack"
            value={pack?.name ?? "Not selected"}
            icon={PackageOpen}
          />
          <SummaryRow label="Plant time" value={plantTimeSummary(state)} icon={Clock} />
          <SummaryRow
            label="First data source"
            value={
              state.claimCode ? "Gateway claim code ready" : "Add a Thing from the Things page"
            }
            icon={Cpu}
          />
        </ul>

        <div className="flex flex-col gap-2 rounded-md border border-dashed border-border bg-surface-elevated/40 p-4">
          <p className="text-sm font-medium text-on-surface">What's next?</p>
          <ul className="grid gap-1.5 text-xs text-on-surface-muted md:grid-cols-2">
            <li>
              📊 Open <code className="font-mono">/app/dashboards</code> — the
              foundry pack ships 5 default dashboards.
            </li>
            <li>
              🧠 Try the AI casting consultant at{" "}
              <code className="font-mono">/app/twin/copilot</code> — ask it to
              "model my plant from scratch".
            </li>
            <li>
              🎨 Tweak branding at{" "}
              <code className="font-mono">/app/admin → Branding</code> — 6
              presets, palette pickers, logo upload.
            </li>
            <li>
              Σ Define a computed tag at{" "}
              <code className="font-mono">/app/computed-tags</code> — start
              from a preset like Pt100 or OEE.
            </li>
          </ul>
        </div>

        {/* Non-blocking discovery of the guided tours (#122 / #124 follow-up).
            New users land here after onboarding — surface the SAME tutorial
            cards the hub shows so they can discover + start a spotlight tour
            right away. Onboarding is a sibling route of /app, so it's OUTSIDE
            <TourProvider> — the cards Start via a <Link> to /app/tutorials/:id
            (TutorialPlayerRoute auto-starts the tour from inside the provider)
            rather than calling useTour().start() here, which would throw. This
            stays optional: the user can still Open dashboards / Build plant. */}
        <OnboardingTutorials />

        <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
          <Button size="sm" variant="ghost" onClick={onResetOnboarding}>
            <RefreshCw className="mr-1.5 size-3.5" />
            Restart wizard
          </Button>
          {foundryNeedsPlant ? (
            <Button size="lg" onClick={onBuildPlant}>
              <Factory className="mr-1.5 size-4" />
              Build your plant
              <ArrowRight className="ml-1.5 size-4" />
            </Button>
          ) : state.pickedPack === "foundry" ? (
            <Button size="lg" onClick={onOpenGraph}>
              <Factory className="mr-1.5 size-4" />
              Open the process graph
              <ArrowRight className="ml-1.5 size-4" />
            </Button>
          ) : (
            <Button size="lg" onClick={onOpenApp}>
              <LayoutDashboard className="mr-1.5 size-4" />
              Open dashboards
              <ArrowRight className="ml-1.5 size-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * OnboardingTutorials — the compact guided-tour gallery shown on the Done step.
 *
 * Reuses the SAME <TutorialCard> the hub (/app/tutorials) renders, so the two
 * stay in sync. Onboarding sits OUTSIDE the /app shell's <TourProvider>, so the
 * cards run in LINK mode (no `onStart`): Start is a <Link> to /app/tutorials/:id
 * whose TutorialPlayerRoute starts the tour from inside the provider. This keeps
 * useTour() out of onboarding entirely (calling it here would throw). Completion
 * ticks are read once per mount from localStorage, matching the hub.
 */
function OnboardingTutorials() {
  const doneIds = useMemo(() => new Set(getDoneIds()), []);
  const doneCount = TUTORIALS.filter((t) => doneIds.has(t.id)).length;

  const grouped = useMemo(() => {
    const map = new Map<TutorialGroup, TutorialDefinition[]>();
    for (const t of TUTORIALS) {
      const list = map.get(t.group) ?? [];
      list.push(t);
      map.set(t.group, list);
    }
    return map;
  }, []);

  return (
    <section className="flex flex-col gap-3 rounded-md border border-dashed border-border bg-surface-elevated/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-brand-muted text-brand">
            <GraduationCap className="size-5" />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-on-surface">
              Take a guided tour
            </span>
            <span className="text-xs text-on-surface-muted">
              Short, spotlight walkthroughs over the real pages — start one any
              time. The tour only highlights and explains; it never changes data.
            </span>
          </div>
        </div>
        <Badge variant="secondary" className="gap-1.5 shrink-0">
          <CheckCircle2 className="size-3.5" />
          {doneCount} / {TUTORIALS.length} completed
        </Badge>
      </div>

      {GROUP_ORDER.map((group) => {
        const items = grouped.get(group);
        if (!items || items.length === 0) return null;
        return (
          <div key={group} className="flex flex-col gap-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-muted">
              {group}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {items.map((t) => (
                // No `onStart` → LINK mode: Start navigates to /app/tutorials/:id
                // (TutorialPlayerRoute), which starts the tour inside the
                // provider. Required since onboarding is outside <TourProvider>.
                <TutorialCard key={t.id} tutorial={t} done={doneIds.has(t.id)} />
              ))}
            </div>
          </div>
        );
      })}

      <Link
        to="/app/settings?tab=help"
        className="flex items-center gap-1 self-start text-xs font-medium text-brand hover:underline"
      >
        See all in Tutorials
        <ArrowRight className="size-3.5" />
      </Link>
    </section>
  );
}

function SummaryRow({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: typeof Building2;
}) {
  return (
    <li className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-elevated px-3 py-2">
      <div className="flex items-center gap-2">
        <Icon className="size-3.5 text-on-surface-muted" />
        <span className="text-xs text-on-surface-muted">{label}</span>
      </div>
      <span className="text-xs font-medium text-on-surface">{value}</span>
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

function LabelledField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

/**
 * Parse a pasted invite into a bare token. Accepts either a full invite URL
 * (…/accept-invite?token=XYZ) or a raw token string. Returns null for empty.
 */
function extractInviteToken(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  try {
    const u = new URL(s);
    const t = u.searchParams.get("token");
    if (t) return t;
  } catch {
    /* not a URL — fall through and treat the whole string as the token */
  }
  return s;
}

/**
 * Client-side slug (mirrors orgs.slugify in the backend): lowercase, spaces
 * → '-', strip non [a-z0-9-], collapse dash runs, trim. Used for the site
 * slug; the backend re-derives the org slug from the name itself.
 */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]+/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Map an onboarding API failure to a short, friendly toast message. */
function onboardingErrorMessage(e: unknown, step: number): string {
  const verb =
    step === 2
      ? "create your organisation"
      : step === 3
        ? "enable the pack"
        : step === STEP_PLANT_TIME
          ? "save the plant time"
          : "claim the gateway";
  // A step that would write to another org than the wizard's stops with its own words.
  const wrongOrg = wizardOrgErrorText(e);
  if (wrongOrg) return wrongOrg;
  // The plant time's own refusal says what to fix, in the platform's words.
  if (step === STEP_PLANT_TIME && e instanceof ApiClientError && e.status === 400) return plantTimeSaveErrorText(e);
  if (e instanceof ApiClientError) {
    const code = (e.body as { error?: string } | null)?.error;
    if (code === "org_slug_taken")
      return "That organisation name is taken — try a slightly different name.";
    if (e.status === 403) return `You don't have permission to ${verb}.`;
    if (e.status === 401) return "Your session expired — please sign in again.";
    if (code) return `Couldn't ${verb} (${code}). Please try again.`;
  }
  return `Couldn't ${verb}. Please try again.`;
}

/**
 * Cosmetic stub claim code for the fixture-demo build (`!live`). Live mode mints
 * the real code server-side (`things.claim` → ~32-char base64url). Shaped like
 * the live code so the demo never teaches the wrong format (#238).
 */
function makeClaimCode(): string {
  const alphabet =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_"; // base64url
  return Array.from(
    { length: 32 },
    () => alphabet[Math.floor(Math.random() * alphabet.length)],
  ).join("");
}

function loadState(): OnboardingState {
  if (typeof window === "undefined") return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    const merged = { ...DEFAULT_STATE, ...parsed };
    // A state saved before the Plant time step numbered Connect 4 and Done 5.
    if (parsed?.version !== STATE_VERSION) {
      if (merged.step === 4) merged.step = STEP_CONNECT;
      else if (merged.step === 5) merged.step = STEP_DONE;
      merged.version = STATE_VERSION;
    }
    // Sanitize a stale pickedPack: testers who picked the now-removed pharma/
    // food packs before this change would otherwise resume with an invalid
    // value. Reset anything that isn't a currently-offered pack.
    if (merged.pickedPack !== "foundry" && merged.pickedPack !== "none") {
      merged.pickedPack = null;
    }
    return merged;
  } catch {
    return DEFAULT_STATE;
  }
}

function saveState(state: OnboardingState) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota — ignore */
  }
}

function clearState() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Clear persisted onboarding progress so a fresh run starts at step 1.
 * Used by the org switcher's "Create organization" entry — otherwise a
 * user who already finished onboarding would resume at the (stale) summary
 * step for their previous org.
 */
export function resetOnboardingState() {
  clearState();
}

